import { createContext, useContext } from 'react';
import type { BenchMetric, BenchMetricKey, Leaderboard, PeerSet, PeerTier } from '@aithora/shared';
import type { QueryState } from '../../useQuery';

/** One graded building, the same shape whether it came from the leaderboard or /my-facility/benchmark. */
export interface Bench {
  /** The facility ORG id (null for a Facility Admin's own building, which the server pins). */
  orgId: string | null;
  name: string;
  ccn: string | null;
  beds: number | null;
  composite: number | null;
  grade: string | null;
  peerN: number | null;
  insufficient: boolean;
  metrics: Partial<Record<BenchMetricKey, BenchMetric>>;
  residents: number | null;
}

export type TabId = 'overview' | 'survey' | 'creds' | 'admit' | 'careops' | 'rebates' | 'portfolio' | 'distribution' | 'trajectory';

/** What every tab of Performance reads: who is looking, the peer set, and the chosen building. */
export interface PerfCtx {
  /** Operator, Super Admin or Partner: sees every building (Portfolio, the picker). */
  op: boolean;
  /** Facility Admin: their own building only, no picker. */
  fa: boolean;
  /** Partner, or viewing as another role: reads, never writes. */
  ro: boolean;
  /** May press write buttons. */
  canAct: boolean;
  roReason: string | null;
  tier: PeerTier;
  caseMix: boolean;
  /** Switch the case-mix adjustment (the header and the heatmap share it). */
  setCaseMix: (on: boolean) => void;
  /** The building on screen, or null while it loads. */
  fac: Bench | null;
  facState: { loading: boolean; error: string | null };
  /** Operator only: every building the operator manages, graded (the leaderboard). */
  board: QueryState<Leaderboard> | null;
  /** The peer set the grades rest on, when the server says (PROPOSED); else national with the scorer's n. */
  peer: PeerSet | null;
  /** "national peers (n=14,709)" (design peerLabel, L35034). */
  peerLabel: string;
  /** The bed filter Portfolio, Distribution and Trajectory share (design beds). */
  beds: 'All' | '<100' | '100+';
  setBeds: (b: 'All' | '<100' | '100+') => void;
  /** Open another tab (design set({ tab })). */
  goTab: (t: TabId) => void;
  /** Pick a building by its org id (Operator; Portfolio rows use it). */
  pickFacility: (orgId: string) => void;
  /** The facility switcher's pick as ?facilities= (Operator, Super Admin), or undefined for every building. */
  facilities?: string;
}

export const PerfContext = createContext<PerfCtx | null>(null);
export function usePerf(): PerfCtx {
  const v = useContext(PerfContext);
  if (!v) throw new Error('usePerf must be used inside the Performance page');
  return v;
}
